/**
 * Platform Shell API Service
 * Handles communication with Identity Service and Notification Service
 */

import type {
  App,
  Notification,
  NotificationPreference,
  User,
  UserStatus,
  UserPresenceUpdate,
} from '../types';

// Dashboard response from backend
export interface PortalDashboard {
  apps: App[];
  recentApps: App[];
  categories: string[];
  user?: {
    id: string;
    email: string;
    name?: string;
    avatar?: string;
  };
  organizations: Array<{
    id: string;
    name: string;
    slug?: string;
  }>;
}

// App launch response
export interface AppLaunchResponse {
  sso_url: string;
  app_url: string;
  requires_exchange: boolean;
}

// Token exchange response
export interface TokenExchangeResponse {
  token: string;
  expires_in: number;
  app_url: string;
}

/**
 * Error thrown by every fetch helper in this module.
 *
 * `message` is unchanged from before (the server's `detail`, or `HTTP <code>`)
 * so existing consumers that only read `e.message` keep working. `status` is
 * additive, and exists because callers could not previously tell "you are not
 * authorised" from "there is no such endpoint" -- both arrived as a bare
 * Error, and the notification bell rendered either one as "Connecting...".
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;

  constructor(message: string, status: number, url: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
  }
}

let apiBaseUrl = '';
let presenceBaseUrl = '';
let notificationsBaseUrl = '';
let identityBaseUrl = 'https://identity.moyasolutions.com';
let authToken = '';

export function configure(baseUrl: string, token?: string) {
  // Remove trailing slash and /api/v1 suffix if present (to avoid doubling)
  apiBaseUrl = baseUrl.replace(/\/$/, '').replace(/\/api\/v1$/, '');
  if (token) {
    authToken = token;
  }
}

export function configurePresence(baseUrl: string) {
  // Configure separate URL for presence/status API (Platform Service)
  presenceBaseUrl = baseUrl.replace(/\/$/, '').replace(/\/api\/v1$/, '');
}

export function configureNotifications(baseUrl: string) {
  // Configure separate URL for the Notification Service.
  //
  // Notifications are their own service on their own host
  // (https://notify.moyasolutions.com) and are NOT served by whatever the
  // host app happens to pass to configure(). Until this existed, every
  // notificationsAPI call inherited apiBaseUrl, so an app that configured the
  // shell with its own API -- or with Identity -- sent
  // /api/v1/notifications/* to a service that has no such routes and got a
  // 404 on every poll.
  //
  // Unset is still valid: fetchNotificationsAPI falls back to apiBaseUrl, so
  // apps that genuinely proxy notifications through their main API are
  // unaffected by this being added.
  notificationsBaseUrl = baseUrl.replace(/\/$/, '').replace(/\/api\/v1$/, '');
}

export function configureIdentity(baseUrl: string) {
  // Configure separate URL for Identity Service (auth endpoints)
  identityBaseUrl = baseUrl.replace(/\/$/, '').replace(/\/api\/v1$/, '');
}

export function getIdentityBaseUrl(): string {
  return identityBaseUrl;
}

export function setToken(token: string) {
  authToken = token;
}

export function getToken(): string {
  return authToken;
}

export function isConfigured(): boolean {
  return !!apiBaseUrl;
}

export function getApiBaseUrl(): string {
  return apiBaseUrl;
}

export function getPresenceBaseUrl(): string {
  return presenceBaseUrl || apiBaseUrl;
}

export function getNotificationsBaseUrl(): string {
  return notificationsBaseUrl || apiBaseUrl;
}

async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Ensure API is configured before making requests
  if (!apiBaseUrl) {
    throw new Error('API not configured. Call configure() first.');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new ApiError(error.detail || `HTTP ${response.status}`, response.status, apiBaseUrl);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// Fetch for Identity Service - uses identityBaseUrl for auth endpoints
async function fetchIdentityAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  if (!identityBaseUrl) {
    throw new Error('Identity API not configured.');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${identityBaseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new ApiError(error.detail || `HTTP ${response.status}`, response.status, identityBaseUrl);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// Portal API - matches /api/v1/portal endpoints
export const portalAPI = {
  /**
   * Get dashboard data including apps, recent apps, categories, user info
   * Falls back to /portal/apps if dashboard fails
   */
  getDashboard: async (): Promise<PortalDashboard> => {
    try {
      return await fetchAPI('/api/v1/portal/dashboard');
    } catch {
      // Fallback: use /portal/apps endpoint which has less data but is more stable
      console.log('[PortalAPI] Dashboard failed, falling back to /portal/apps');
      const apps = await fetchAPI<App[]>('/api/v1/portal/apps');
      return {
        apps,
        recentApps: [],
        categories: [...new Set(apps.map(a => a.category).filter((c): c is string => !!c))],
        organizations: [],
      };
    }
  },

  /**
   * Get list of apps user can access
   * @param category - Optional category filter
   * @param search - Optional search query
   */
  getApps: (params?: { category?: string; search?: string }): Promise<App[]> => {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.set('category', params.category);
    if (params?.search) queryParams.set('search', params.search);
    const query = queryParams.toString();
    return fetchAPI(`/api/v1/portal/apps${query ? `?${query}` : ''}`);
  },

  /**
   * Launch an app and get SSO URL
   */
  launchApp: (appId: string): Promise<AppLaunchResponse> =>
    fetchAPI(`/api/v1/portal/apps/${appId}/launch`, { method: 'POST' }),

  /**
   * Toggle app as favorite
   */
  toggleFavorite: (appId: string): Promise<{ is_favorite: boolean; app_id: string }> =>
    fetchAPI(`/api/v1/portal/apps/${appId}/favorite`, { method: 'POST' }),

  /**
   * Switch active context (app/admin/portal)
   */
  switchContext: (context: 'app' | 'admin' | 'portal'): Promise<{ success: boolean; context: string }> =>
    fetchAPI('/api/v1/portal/context/switch', {
      method: 'POST',
      body: JSON.stringify({ context }),
    }),

  /**
   * Exchange token for a target app
   */
  exchangeToken: (targetAppId: string): Promise<TokenExchangeResponse> =>
    fetchAPI('/api/v1/portal/auth/token/exchange', {
      method: 'POST',
      body: JSON.stringify({
        target_app_id: targetAppId,
        source_token: authToken,
      }),
    }),

  /**
   * Get current user info
   */
  getCurrentUser: (): Promise<User> =>
    fetchAPI('/api/v1/portal/me'),

  /**
   * Switch active organization
   */
  switchOrganization: (organizationId: string): Promise<{ success: boolean; organization_id: string }> =>
    fetchAPI('/api/v1/portal/organization/switch', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    }),

  /**
   * Get organization branding
   */
  getOrganizationBranding: (organizationId: string): Promise<{
    logo_url?: string;
    primary_color?: string;
    secondary_color?: string;
    favicon_url?: string;
  }> =>
    fetchAPI(`/api/v1/portal/organizations/${organizationId}/branding`),
};

// Auth API - uses Identity Service for auth endpoints
export const authAPI = {
  /**
   * Get current user info from Identity Service
   */
  getCurrentUser: (): Promise<User> =>
    fetchIdentityAPI('/api/v1/auth/whoami'),

  /**
   * Exchange SSO token for app token
   */
  exchangeSSOToken: (ssoToken: string): Promise<{ access_token: string; user: User }> =>
    fetchIdentityAPI('/api/v1/auth/sso/exchange', {
      method: 'POST',
      body: JSON.stringify({ sso_token: ssoToken }),
    }),

  /**
   * Logout current session
   */
  logout: (): Promise<void> =>
    fetchIdentityAPI('/api/v1/auth/logout', { method: 'POST' }),

  /**
   * Revoke all sessions
   */
  revokeAllSessions: (): Promise<void> =>
    fetchIdentityAPI('/api/v1/auth/sessions/revoke-all', { method: 'POST' }),
};

// Notification-specific fetch that uses notificationsBaseUrl if configured.
// Same shape as fetchPresenceAPI below: a dedicated base URL with a fallback
// to apiBaseUrl, so nothing breaks for apps that never call
// configureNotifications().
async function fetchNotificationsAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const baseUrl = notificationsBaseUrl || apiBaseUrl;
  if (!baseUrl) {
    throw new Error(
      'Notifications API not configured. Call configure() or configureNotifications() first.'
    );
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new ApiError(error.detail || `HTTP ${response.status}`, response.status, baseUrl);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Notification Service wire formats
//
// The service is FastAPI and answers in snake_case; this package's public
// `Notification` type is camelCase. Nothing translated between the two, so
// even once the requests reach the right host the replies were unreadable:
// `read`, `createdAt`, `actionUrl` and `sourceApp` were all `undefined`.
// That renders as every notification permanently unread, an "Invalid Date"
// timestamp, and a click that navigates nowhere.
//
// Two further shape mismatches have the same origin:
//
//   - `GET /notifications` returns an ENVELOPE -- `{ notifications, total,
//     unread_count }` -- not a bare array. Assigning that object straight to
//     a `Notification[]` left `.length === undefined`, so the bell rendered
//     "No notifications" over a perfectly good 200. That is the failure mode
//     a host-only fix would have landed in: green request, empty bell.
//
//   - `GET /preferences` returns a LIST of typed objects, not a map, and
//     there is no collection-level write at all: `PUT /preferences` answers
//     405 because `GET /preferences` occupies that path. Writes go one per
//     type code, to `PUT /preferences/{type_code}`.
//
// Every adapter below accepts the previously-assumed shape as well, so an app
// pointing the shell at a different notification backend keeps working.
// ---------------------------------------------------------------------------

interface RawNotification {
  id: string;
  title: string;
  body?: string | null;
  icon?: string | null;
  priority?: string | null;
  type?: string | null;
  // Notification Service (snake_case) ... / previously-assumed (camelCase)
  action_url?: string | null;
  actionUrl?: string | null;
  source_app_name?: string | null;
  sourceApp?: string | null;
  is_read?: boolean;
  read?: boolean;
  created_at?: string | null;
  createdAt?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface RawNotificationList {
  notifications?: RawNotification[];
  total?: number;
  unread_count?: number;
}

interface RawPreference {
  type_code: string;
  type_name?: string;
  enabled: boolean;
  channels?: string[] | null;
}

const NOTIFICATION_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
type NotificationPriority = (typeof NOTIFICATION_PRIORITIES)[number];

function toPriority(value: unknown): NotificationPriority {
  return (NOTIFICATION_PRIORITIES as readonly string[]).includes(value as string)
    ? (value as NotificationPriority)
    : 'normal';
}

/** Normalise one wire notification into this package's public shape. */
export function adaptNotification(raw: RawNotification): Notification {
  return {
    id: String(raw.id),
    title: raw.title,
    body: raw.body ?? undefined,
    icon: raw.icon ?? undefined,
    priority: toPriority(raw.priority),
    type: raw.type ?? undefined,
    sourceApp: raw.source_app_name ?? raw.sourceApp ?? undefined,
    actionUrl: raw.action_url ?? raw.actionUrl ?? undefined,
    actionData: raw.metadata ?? undefined,
    // The service's field is `is_read`. It also exposes a separate nullable
    // `read_at` timestamp, which is NOT the boolean -- reading that instead
    // would make every unread notification look read.
    read: raw.is_read ?? raw.read ?? false,
    // Left empty rather than defaulted to "now": a missing timestamp should
    // render as no timestamp, not as a plausible lie. The bell's formatTime
    // returns '' for an unparseable value.
    createdAt: raw.created_at ?? raw.createdAt ?? '',
  };
}

// Notifications API - matches /api/v1/notifications endpoints on the
// Notification Service. Uses configureNotifications() when set, otherwise
// falls back to the generic API base URL.
export const notificationsAPI = {
  /**
   * Get notifications list.
   *
   * Accepts either the service's `{ notifications, total, unread_count }`
   * envelope or a bare array, and returns this package's camelCase shape.
   */
  getNotifications: async (params?: {
    limit?: number;
    unread_only?: boolean;
  }): Promise<Notification[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.unread_only) queryParams.set('unread_only', 'true');
    const query = queryParams.toString();
    // No trailing slash: the route is declared as `@router.get("")`, and
    // `/notifications/` answers 307 to a plain-http URL, across which the
    // browser drops the Authorization header.
    const raw = await fetchNotificationsAPI<RawNotification[] | RawNotificationList>(
      `/api/v1/notifications${query ? `?${query}` : ''}`
    );
    const list = Array.isArray(raw) ? raw : raw?.notifications ?? [];
    return list.map(adaptNotification);
  },

  /**
   * Get unread count.
   *
   * The service answers `{ count }` here but calls the same number
   * `unread_count` inside the list envelope; both are accepted.
   */
  getUnreadCount: async (): Promise<{ count: number }> => {
    const raw = await fetchNotificationsAPI<{ count?: number; unread_count?: number }>(
      '/api/v1/notifications/unread/count'
    );
    return { count: raw?.count ?? raw?.unread_count ?? 0 };
  },

  /**
   * Mark notification as read
   */
  markAsRead: (notificationId: string): Promise<void> =>
    fetchNotificationsAPI(`/api/v1/notifications/${notificationId}/read`, { method: 'POST' }),

  /**
   * Mark all notifications as read
   */
  markAllAsRead: (): Promise<void> =>
    fetchNotificationsAPI('/api/v1/notifications/read-all', { method: 'POST' }),

  /**
   * Dismiss notification
   */
  dismiss: (notificationId: string): Promise<void> =>
    fetchNotificationsAPI(`/api/v1/notifications/${notificationId}/dismiss`, { method: 'POST' }),

  /**
   * Get notification preferences as a `{ typeCode: enabled }` map.
   *
   * The service returns a list of typed objects; it is flattened here so the
   * returned shape matches what `updatePreferences` accepts.
   */
  getPreferences: async (): Promise<Record<string, boolean>> => {
    const raw = await fetchNotificationsAPI<RawPreference[] | Record<string, boolean>>(
      '/api/v1/notifications/preferences'
    );
    if (!Array.isArray(raw)) {
      return raw ?? {};
    }
    return Object.fromEntries(raw.map((p) => [p.type_code, p.enabled]));
  },

  /**
   * Get notification preferences with their full detail (display name and
   * delivery channels), rather than the flattened enabled-only map.
   */
  getPreferenceDetails: async (): Promise<NotificationPreference[]> => {
    const raw = await fetchNotificationsAPI<RawPreference[] | Record<string, boolean>>(
      '/api/v1/notifications/preferences'
    );
    if (!Array.isArray(raw)) {
      return Object.entries(raw ?? {}).map(([type, enabled]) => ({
        type,
        enabled,
        channels: [],
      }));
    }
    return raw.map((p) => ({
      type: p.type_code,
      enabled: p.enabled,
      channels: (p.channels ?? []) as NotificationPreference['channels'],
    }));
  },

  /**
   * Update notification preferences.
   *
   * There is no collection-level write on the service -- `PUT /preferences`
   * answers 405 -- so each type code is written individually. Sending the
   * whole map to `/preferences` silently persisted nothing.
   */
  updatePreferences: async (preferences: Record<string, boolean>): Promise<void> => {
    await Promise.all(
      Object.entries(preferences).map(([typeCode, enabled]) =>
        fetchNotificationsAPI(
          `/api/v1/notifications/preferences/${encodeURIComponent(typeCode)}`,
          {
            method: 'PUT',
            body: JSON.stringify({ enabled }),
          }
        )
      )
    );
  },
};

// Presence-specific fetch that uses presenceBaseUrl if configured
async function fetchPresenceAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const baseUrl = presenceBaseUrl || apiBaseUrl;
  if (!baseUrl) {
    throw new Error('Presence API not configured. Call configure() or configurePresence() first.');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new ApiError(error.detail || `HTTP ${response.status}`, response.status, baseUrl);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// Presence API - user status and presence management
// Uses Platform Service if configured via configurePresence(), otherwise falls back to main API
export const presenceAPI = {
  /**
   * Update current user's status
   */
  updateStatus: (status: UserStatus, statusMessage?: string): Promise<UserPresenceUpdate> =>
    fetchPresenceAPI('/api/v1/users/me/status', {
      method: 'POST',
      body: JSON.stringify({ status, status_message: statusMessage }),
    }),

  /**
   * Get a user's current status
   */
  getStatus: (userId: string): Promise<UserPresenceUpdate> =>
    fetchPresenceAPI(`/api/v1/users/${userId}/status`),

  /**
   * Get current user's status
   */
  getMyStatus: (): Promise<UserPresenceUpdate> =>
    fetchPresenceAPI('/api/v1/users/me/status'),

  /**
   * Clear custom status message (keeps status)
   */
  clearStatusMessage: (): Promise<UserPresenceUpdate> =>
    fetchPresenceAPI('/api/v1/users/me/status/message', { method: 'DELETE' }),
};

// Settings API - user preferences
export const settingsAPI = {
  /**
   * Get user settings
   */
  getSettings: (): Promise<Record<string, unknown>> =>
    fetchAPI('/api/v1/users/me/settings'),

  /**
   * Update a single setting
   */
  updateSetting: (key: string, value: unknown): Promise<Record<string, unknown>> =>
    fetchAPI('/api/v1/users/me/settings', {
      method: 'PATCH',
      body: JSON.stringify({ [key]: value }),
    }),

  /**
   * Update multiple settings
   */
  updateSettings: (settings: Record<string, unknown>): Promise<Record<string, unknown>> =>
    fetchAPI('/api/v1/users/me/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
};

export default {
  configure,
  configurePresence,
  configureNotifications,
  configureIdentity,
  setToken,
  getToken,
  portal: portalAPI,
  auth: authAPI,
  notifications: notificationsAPI,
  presence: presenceAPI,
  settings: settingsAPI,
};
